import React, { FC, MouseEvent, KeyboardEvent } from 'react'
import classnames from 'classnames/bind'
import Button from '../button'
import type { ButtonProps } from '../button'
import Icon from '../icon'
import {
  useCopyToClipboard,
  CopyStatus,
} from '../../../hooks/useCopyToClipboard'
import type { AutomationTarget } from '../../../types'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface CopyButtonProps extends AutomationTarget {
  text: string
  onCopy?: (text: string) => void
  onError?: (error: Error) => void
  buttonProps?: Partial<ButtonProps>
  ariaLabel?: string
  successMessage?: string
  size?: 'small' | 'medium' | 'large'
  variant?: 'icon' | 'text' | 'both'
  iconOnly?: boolean
  className?: string
  disabled?: boolean
  showSuccessFeedback?: boolean
  showSuccessTextInButton?: boolean
  defaultIconName?: string
}

const getIconName = (status: CopyStatus, defaultIconName: string): string => {
  switch (status) {
    case 'success':
      return 'check'
    case 'error':
      return 'exclamation-circle'
    case 'copying':
      return 'spinner'
    default:
      return defaultIconName
  }
}

const getButtonText = (
  status: CopyStatus,
  variant: string,
  successMessage?: string,
  showSuccessTextInButton?: boolean
): string | undefined => {
  // If icon variant and we want to show success text in button
  if (variant === 'icon' && showSuccessTextInButton && status === 'success') {
    return successMessage || 'Copied!'
  }

  if (variant === 'icon') return undefined

  switch (status) {
    case 'success':
      return successMessage || 'Copied!'
    case 'error':
      return 'Failed'
    case 'copying':
      return 'Copying...'
    default:
      return 'Copy'
  }
}

export const CopyButton: FC<CopyButtonProps> = ({
  text,
  onCopy,
  onError,
  buttonProps = {},
  ariaLabel,
  successMessage,
  size = 'medium',
  variant = 'icon',
  iconOnly = variant === 'icon',
  className,
  disabled = false,
  showSuccessFeedback = false,
  showSuccessTextInButton = false,
  elmId,
  defaultIconName = 'copy',
}) => {
  const { copy, status } = useCopyToClipboard({
    onSuccess: onCopy,
    onError,
    resetDelay: showSuccessFeedback ? 1500 : 2000,
  })

  const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      await copy(text)
    }
  }

  const handleKeyDown = async (e: KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault()
      e.stopPropagation()
      await copy(text)
    }
  }

  const iconName = getIconName(status, defaultIconName)
  const buttonText = getButtonText(
    status,
    variant,
    successMessage,
    showSuccessTextInButton
  )
  const isLoading = status === 'copying'

  const effectiveAriaLabel =
    ariaLabel || (iconOnly ? 'Copy to clipboard' : undefined)

  const button = (
    <Button
      {...buttonProps}
      className={cx(
        'copy-button',
        `copy-button--${variant}`,
        `copy-button--${status}`,
        size,
        {
          'copy-button--has-text': buttonText && variant === 'icon',
        },
        className
      )}
      size={size}
      role='tertiary'
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={effectiveAriaLabel}
      disabled={disabled || isLoading}
      loading={isLoading}
      elmId={elmId}
      collapse={iconOnly}
    >
      <span className={cx('copy-button-icon-wrapper')}>
        <Icon
          name={iconName}
          iconStyle={status === 'success' ? 'solid' : 'regular'}
          className={cx('copy-button-icon', {
            'copy-button-icon--spinning': isLoading,
          })}
        />
      </span>
      {buttonText && (
        <span className={cx('copy-button-text')}>{buttonText}</span>
      )}
    </Button>
  )

  if (showSuccessFeedback && status === 'success') {
    return (
      <div className={cx('copy-button-wrapper')}>
        {button}
        <span className={cx('copy-button-feedback')}>
          {successMessage || 'Copied'}
        </span>
      </div>
    )
  }

  return button
}

export default CopyButton
